import React, { Component } from 'react';
import { API, graphqlOperation } from 'aws-amplify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUndo, faCheck, faCircle } from '@fortawesome/free-solid-svg-icons';
import * as queries from '../graphql/queries';
import { progressOf } from './progress';
import { shortId } from './order';
import './timeline.css';

const WEEKS = 26;
const DAY_MS = 24 * 60 * 60 * 1000;
const GROUPS_PER_PAGE = 10;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Local-date key, so "today" matches the viewer's calendar rather than UTC.
const dayKey = (date) =>
  date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0');

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

// There is no completedAt field; completed tasks can't be edited, so updatedAt is the completion time.
const completedAt = (task) => (task.updatedAt ? new Date(task.updatedAt) : null);

const weight = (task) => Math.max(progressOf(task).estimate, 1);

function formatDay(key) {
  const [y, m, d] = key.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const today = startOfDay(new Date());
  const diff = Math.round((today - date) / DAY_MS);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  const label = date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  return date.getFullYear() === today.getFullYear() ? label : label + ', ' + date.getFullYear();
}

function streaks(byDay) {
  let longest = 0;
  let run = 0;
  let prev = null;
  Object.keys(byDay).sort().forEach((key) => {
    const [y, m, d] = key.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    run = prev && Math.round((date - prev) / DAY_MS) === 1 ? run + 1 : 1;
    longest = Math.max(longest, run);
    prev = date;
  });

  // Current streak counts back from today, or from yesterday if nothing is done yet today.
  let current = 0;
  let cursor = startOfDay(new Date());
  if (!byDay[dayKey(cursor)]) cursor = new Date(cursor.getTime() - DAY_MS);
  while (byDay[dayKey(cursor)]) {
    current += 1;
    cursor = new Date(cursor.getTime() - DAY_MS);
  }
  return { current, longest };
}

// Loads a task's subtask tree (a few levels deep) for the expanded log entry.
async function loadSubtasks(parentId, depth) {
  const res = await API.graphql(graphqlOperation(queries.listTasks, { filter: { parentTaskId: { eq: parentId } }, limit: 999999999 }));
  const items = res.data.listTasks.items.filter((t) => t.deletionState !== 'deleted');
  if (depth > 1) {
    await Promise.all(items.map(async (t) => { t.children = await loadSubtasks(t.id, depth - 1); }));
  }
  return items;
}

function SubtaskTree({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <ul className="log__tree">
      {items.map((t) => {
        const { done, estimate } = progressOf(t);
        const finished = t.state === 'completed' || (estimate > 0 && done >= estimate);
        return (
          <li key={t.id}>
            <span className={'log__tree-row' + (finished ? ' is-done' : '')}>
              <FontAwesomeIcon icon={finished ? faCheck : faCircle} className="log__tree-icon" />
              <span className="log__tree-name">{t.name}</span>
              <span className="log__tree-units">{done}/{estimate}</span>
            </span>
            <SubtaskTree items={t.children} />
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Completed-work history: a contribution heatmap (shaded by units shipped) above a
 * commit-log style list grouped by day. Clicking a heatmap day filters the log.
 */
class Timeline extends Component {
  state = { selected: null, pages: 1, openId: null, subtasks: {}, undoing: null };

  toggleOpen = async (task) => {
    const openId = this.state.openId === task.id ? null : task.id;
    this.setState({ openId });
    if (openId && this.state.subtasks[task.id] === undefined) {
      this.setState({ subtasks: { ...this.state.subtasks, [task.id]: null } });
      try {
        const items = await loadSubtasks(task.id, 3);
        this.setState({ subtasks: { ...this.state.subtasks, [task.id]: items } });
      } catch (err) {
        this.setState({ subtasks: { ...this.state.subtasks, [task.id]: [] } });
      }
    }
  }

  undo = async (task) => {
    this.setState({ undoing: task.id });
    try {
      await this.props.onUndo(task);
    } finally {
      this.setState({ undoing: null, openId: null });
    }
  }

  renderDetail(task) {
    const subtasks = this.state.subtasks[task.id];
    const { done, estimate } = progressOf(task);
    return (
      <div className="log__detail">
        <div className="log__full-name">{task.name}</div>
        <div className="log__meta">
          <code title={task.id}>{task.id}</code>
          <span>{done}/{estimate} units</span>
          <span>Completed {completedAt(task).toLocaleString()}</span>
        </div>
        {subtasks === null || subtasks === undefined
          ? <div className="log__loading">Loading subtasks…</div>
          : subtasks.length > 0
            ? <SubtaskTree items={subtasks} />
            : <div className="log__loading">No subtasks</div>}
        {this.props.onUndo && (
          <button
            className="btn btn--ghost btn--sm log__undo"
            onClick={() => this.undo(task)}
            disabled={this.state.undoing === task.id}
          >
            <FontAwesomeIcon icon={faUndo} /> {this.state.undoing === task.id ? 'Moving back…' : 'Mark not done'}
          </button>
        )}
      </div>
    );
  }

  selectDay = (key) => {
    this.setState({ selected: this.state.selected === key ? null : key, pages: 1 });
  }

  buildDays() {
    const byDay = {};
    this.props.tasks.forEach((task) => {
      const at = completedAt(task);
      if (!at || isNaN(at)) return;
      const key = dayKey(at);
      if (!byDay[key]) byDay[key] = { tasks: [], units: 0 };
      byDay[key].tasks.push(task);
      byDay[key].units += weight(task);
    });
    Object.keys(byDay).forEach((key) => byDay[key].tasks.sort((a, b) => completedAt(b) - completedAt(a)));
    return byDay;
  }

  renderHeatmap(byDay) {
    const today = startOfDay(new Date());
    const start = new Date(today.getTime() - ((WEEKS - 1) * 7 + today.getDay()) * DAY_MS);
    const max = Math.max(1, ...Object.keys(byDay).map((k) => byDay[k].units));
    const level = (units) => (units ? Math.min(4, Math.ceil((units / max) * 4)) : 0);

    const weeks = [];
    const months = [];
    for (let w = 0; w < WEEKS; w++) {
      const cells = [];
      for (let d = 0; d < 7; d++) {
        const date = new Date(start.getTime() + (w * 7 + d) * DAY_MS);
        if (d === 0 && (w === 0 || date.getDate() <= 7)) months.push({ w, label: MONTHS[date.getMonth()] });
        if (date > today) {
          cells.push(<span key={d} className="heat__cell heat__cell--future" />);
          continue;
        }
        const key = dayKey(date);
        const day = byDay[key];
        const count = day ? day.tasks.length : 0;
        cells.push(
          <button
            key={d}
            className={'heat__cell heat__cell--' + level(day ? day.units : 0) + (this.state.selected === key ? ' is-selected' : '')}
            onClick={() => this.selectDay(key)}
            title={formatDay(key) + ': ' + (count ? count + ' task' + (count > 1 ? 's' : '') + ', ' + day.units + ' units' : 'nothing completed')}
            aria-label={formatDay(key) + ', ' + count + ' completed'}
          />
        );
      }
      weeks.push(<div key={w} className="heat__week">{cells}</div>);
    }

    return (
      <div className="heat">
        <div className="heat__months" style={{ gridTemplateColumns: `repeat(${WEEKS}, var(--heat-cell))` }}>
          {months.map((m) => <span key={m.w} style={{ gridColumnStart: m.w + 1 }}>{m.label}</span>)}
        </div>
        <div className="heat__body">
          <div className="heat__days"><span /><span>Mon</span><span /><span>Wed</span><span /><span>Fri</span><span /></div>
          <div className="heat__grid">{weeks}</div>
        </div>
        <div className="heat__legend">
          Less {[0, 1, 2, 3, 4].map((l) => <span key={l} className={'heat__cell heat__cell--' + l} />)} More
        </div>
      </div>
    );
  }

  renderStats(byDay) {
    const keys = Object.keys(byDay);
    const { current, longest } = streaks(byDay);
    const units = keys.reduce((n, k) => n + byDay[k].units, 0);
    const best = keys.sort((a, b) => byDay[b].units - byDay[a].units)[0];
    return (
      <dl className="timeline__stats">
        <div><dt>Shipped</dt><dd>{this.props.tasks.length}</dd></div>
        <div><dt>Units</dt><dd>{units}</dd></div>
        <div><dt>Streak</dt><dd>{current}<span>d</span>{current > 0 && <i className="timeline__flame" aria-hidden="true" />}</dd></div>
        <div><dt>Longest</dt><dd>{longest}<span>d</span></dd></div>
        {best && <div><dt>Best day</dt><dd className="timeline__best">{formatDay(best)}</dd></div>}
      </dl>
    );
  }

  renderLog(byDay) {
    const { selected, pages } = this.state;
    const keys = (selected ? [selected] : Object.keys(byDay).sort().reverse()).filter((k) => byDay[k]);
    const shown = keys.slice(0, pages * GROUPS_PER_PAGE);

    if (keys.length === 0) {
      return <p className="timeline__empty">Nothing completed on {formatDay(selected)}.</p>;
    }

    return (
      <div className="log">
        {shown.map((key) => {
          const day = byDay[key];
          return (
            <section key={key} className="log__day">
              <header className="log__head">
                <span className="log__node" aria-hidden="true" />
                <span className="log__date">{formatDay(key)}</span>
                <span className="log__summary">
                  {day.tasks.length} completed · {day.units} unit{day.units === 1 ? '' : 's'}
                </span>
              </header>
              <ul className="log__list">
                {day.tasks.map((task) => (
                  <li key={task.id} className={'log__entry' + (this.state.openId === task.id ? ' is-open' : '')} style={{ '--task-color': task.color }}>
                  <button
                    className="log__item"
                    onClick={() => this.toggleOpen(task)}
                    aria-expanded={this.state.openId === task.id}
                  >
                    <span className="log__dot" aria-hidden="true" />
                    <span className="log__name">{task.name}</span>
                    <code className="log__id" title={task.id}>{shortId(task.id)}</code>
                    <span className="log__units">+{weight(task)}</span>
                    <time className="log__time" dateTime={task.updatedAt}>
                      {completedAt(task).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                    </time>
                  </button>
                  {this.state.openId === task.id && this.renderDetail(task)}
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
        {shown.length < keys.length && (
          <button className="btn btn--ghost btn--sm log__more" onClick={() => this.setState({ pages: pages + 1 })}>
            Show older
          </button>
        )}
      </div>
    );
  }

  render() {
    if (this.props.tasks.length === 0) return null;
    const byDay = this.buildDays();
    return (
      <div className="window timeline">
        <div className="window__bar">
          <span className="window__dots"><i /><i /><i /></span>
          <span className="window__path">progresstodo / history</span>
          {this.state.selected && (
            <button className="timeline__clear" onClick={() => this.selectDay(this.state.selected)}>
              {formatDay(this.state.selected)} ×
            </button>
          )}
        </div>
        <div className="timeline__top">
          {this.renderHeatmap(byDay)}
          {this.renderStats(byDay)}
        </div>
        {this.renderLog(byDay)}
      </div>
    );
  }
}

export default Timeline;
